import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { caseKeys, useCaseDocumentDetail } from '@/hooks/use-case-queries'
import { api } from '@/services/api'
import type { CaseDocumentDetail } from '@/services/api/cases'

// Sentinel pipeline id matching `/components/documents/DocumentList.tsx` —
// the `/v1/cases/:id/extract` endpoint resolves it to the active extraction
// pipeline server-side. Kept inline here to avoid a cross-feature import.
export const REPROCESS_PIPELINE_ID = 'schema-extraction-v2'
export const REPROCESS_PIPELINE_LABEL = 'Schema Extraction (v2)'

interface UseDocumentDetailOverlayArgs {
  caseId: string
  documentId: string
}

interface ReprocessSelectedDoc {
  id: string
  file_type: string | null
  file_size: number
}

interface UseDocumentDetailOverlayResult {
  file: CaseDocumentDetail | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
  reprocessOpen: boolean
  setReprocessOpen: (open: boolean) => void
  isReprocessing: boolean
  reprocessSelectedDocs: ReprocessSelectedDoc[]
  handleReprocess: (schemaName?: string, userPrompt?: string) => Promise<void>
}

/**
 * Container-level data + actions for the document detail overlay. Owns the
 * document detail query, the reprocess (`api.cases.extractDocuments`) mutation
 * and its cache invalidations, and the modal open / loading state — so the
 * overlay component can stay a thin layout orchestrator.
 */
export function useDocumentDetailOverlay({
  caseId,
  documentId,
}: UseDocumentDetailOverlayArgs): UseDocumentDetailOverlayResult {
  const queryClient = useQueryClient()
  const [reprocessOpen, setReprocessOpen] = useState(false)
  const [isReprocessing, setIsReprocessing] = useState(false)

  const detailQuery = useCaseDocumentDetail(caseId, documentId)
  const file = detailQuery.data

  const reprocessSelectedDocs = useMemo<ReprocessSelectedDoc[]>(
    () => (file ? [{ id: file.id, file_type: file.file_type, file_size: file.file_size }] : []),
    [file]
  )

  const handleReprocess = useCallback(
    async (schemaName?: string, userPrompt?: string) => {
      setIsReprocessing(true)
      try {
        await api.cases.extractDocuments(caseId, {
          document_ids: [documentId],
          pipeline: REPROCESS_PIPELINE_ID,
          schema_name: schemaName,
          user_prompt: userPrompt,
        })
        // Refresh the document detail (status flips to processing) and the
        // case-level extraction-runs feed so the new run shows up.
        queryClient.invalidateQueries({
          queryKey: caseKeys.documentDetail(caseId, documentId),
        })
        queryClient.invalidateQueries({ queryKey: caseKeys.extractionRuns(caseId) })
        setReprocessOpen(false)
      } catch (err: unknown) {
        console.error('Reprocess failed:', err)
      } finally {
        setIsReprocessing(false)
      }
    },
    [caseId, documentId, queryClient]
  )

  return {
    file,
    isLoading: detailQuery.isLoading,
    isError: detailQuery.isError,
    refetch: () => {
      void detailQuery.refetch()
    },
    reprocessOpen,
    setReprocessOpen,
    isReprocessing,
    reprocessSelectedDocs,
    handleReprocess,
  }
}
